import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useRef, useState } from 'react'

import { usePairAgeFormatted } from '@root/entities/pair/hooks'
import { getXString } from '@root/entities/socials/model/socials'
import { tradePriceType } from '@root/features/trade/trading/atoms/trading-pair'

import { crown, signedTone } from '../../model/pulse-readings'
import {
  auditVerdict,
  liquidityShare,
  liquidityTone,
  offHigh,
} from '../../model/trade-readings'
import {
  count,
  percent,
  price as priceOf,
  quantity,
  shortAddress,
  signedUsd,
  usd,
} from '../portfolio-screen/money'
import { TokenMark } from '../portfolio-screen/token-mark'

import type { Tone } from '../../model/pulse-readings'
import type { PairModel } from '@root/entities/pair'

const toneVar = (tone: Tone) =>
  ({
    quiet: 'var(--t2)',
    watch: 'var(--warn)',
    loud: 'var(--down)',
    good: 'var(--up)',
    bad: 'var(--down)',
  })[tone]

/**
 * The token, in one line.
 *
 * Twice now this bar has been rejected, and measuring the two terminals we are
 * held against says why both times. Their token headers are one band of about
 * sixty pixels: the identity on the left, then the figure somebody came for,
 * then a run of label-over-value pairs at a sixteen-pixel gap — no boxes, no
 * separators, no second row. gmgn's cells are a 12px label over a 14px value
 * and nothing else; axiom's are the same idea forty pixels shorter.
 *
 * Mine had been a panel: two stacked rows of bordered cells with backgrounds
 * and hairlines between them. That is a spreadsheet, and a spreadsheet at the
 * top of a token page reads as filler no matter how good the numbers in it
 * are. It also stole eighty pixels from the chart, which is the thing on this
 * page anybody actually watches.
 *
 * So: one row, naked figures, and everything that was fighting the chart for
 * room — the flow bar, the risk shares — moved to the rail where both of them
 * keep it.
 */
export const TradeHeader: React.FC<{ pair: PairModel }> = memo(({ pair }) => {
  const [marketCapUsd] = useAtom(pair.marketCapUsd)
  const [liquidityUsd] = useAtom(pair.liquidityUsd)
  const [holdersCount] = useAtom(pair.holdersCount)
  const [audit] = useAtom(pair.audit)
  const [athMarketCap] = useAtom(pair.athMarketCap)
  const [creatorStats] = useAtom(pair.creatorStats)
  const [socials] = useAtom(pair.quote.socials)
  const [supply] = useAtom(pair.supply)
  const [fees] = useAtom(pair.fees.totalFees)
  const [price] = useAtom(pair.price)
  const [bonding] = useAtom(pair.bondingPercentage)
  const [change] = useAtom(pair.stats['1D'].priceChange)
  const [dayVolume] = useAtom(pair.stats['1D'].volumeUsd)
  const [dayNet] = useAtom(pair.stats['1D'].netVolumeInUsd)
  const [dayTx] = useAtom(pair.stats['1D'].transactions)
  const [dayMakers] = useAtom(pair.stats['1D'].makers)

  const setPriceType = useAction(tradePriceType)
  const [priceType] = useAtom(tradePriceType)
  const [auditOpen, setAuditOpen] = useState(false)
  /* Below about 1480 the twelve figures run wider than the band, and they
     scroll rather than wrap. The chevron is how that is discoverable. */
  const figsRef = useRef<HTMLDivElement>(null)

  /*
   * Three controls copy the contract and none of them said so. A click that
   * succeeds silently and a click that fails silently look identical, which is
   * the worst a button can do — the address chip says "copied" for a moment,
   * and only when the write actually resolved.
   */
  const [copied, setCopied] = useState(false)
  const copy = (value: string) => () => {
    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true)
        setTimeout(() => setCopied(false), 1200)
      })
      .catch(() => undefined)
  }

  const { formatted: age } = usePairAgeFormatted(pair.createdAt)

  const links = [
    socials.x ? { kind: 'x' as const, href: getXString(socials.x) } : undefined,
    typeof socials.telegram === 'string'
      ? { kind: 'tg' as const, href: socials.telegram }
      : undefined,
    typeof socials.website === 'string'
      ? { kind: 'web' as const, href: socials.website }
      : undefined,
  ].filter(
    (link): link is { kind: 'x' | 'tg' | 'web'; href: string } =>
      link !== undefined,
  )

  const cap = marketCapUsd.toNumber()
  const liquidity = liquidityUsd.toNumber()
  const share = liquidityShare(liquidity, cap)
  const verdict = auditVerdict(audit.overAllChecks)
  const ath = athMarketCap.toNumber()
  const fromHigh = offHigh(cap, ath)
  const change24h = Number(change)
  const curve = bonding === undefined ? undefined : Number(bonding)
  const launches = crown(creatorStats?.totalPools, creatorStats?.bondedPools)
  const reused = pair.quote.reusedImageCount ?? 0
  const net = dayNet.toNumber()

  return (
    <header className="tokenhead">
      <div className="id">
        <button
          className="markbtn"
          type="button"
          title="Copy the contract address"
          onClick={copy(String(pair.quote.address))}
        >
          <TokenMark
            src={pair.quote.icon.toString()}
            ticker={pair.quote.ticker}
            size={36}
          />
        </button>

        <div className="names">
          <span className="line">
            <button
              className="tickerbtn"
              type="button"
              title="Copy the contract address"
              onClick={copy(String(pair.quote.address))}
            >
              {pair.quote.ticker}
            </button>
            <span className="full">{pair.quote.title}</span>
          </span>
          <span className="line sub">
            <button
              className={copied ? 'chip ghost mono said' : 'chip ghost mono'}
              type="button"
              title="Copy the contract address"
              onClick={copy(String(pair.quote.address))}
            >
              {copied ? 'copied' : shortAddress(String(pair.quote.address), 4)}
            </button>
            <span className="mono age">{age ?? '—'}</span>
            {launches ? (
              <a
                className="chip crown"
                href={`/portfolio-v2?wallet=${audit.meta.deployerAddress ?? ''}`}
                title="What else this creator has launched — open their wallet"
              >
                <CrownIcon /> {launches.text}
              </a>
            ) : undefined}
            {links.map((link) => (
              <a
                key={link.kind}
                className="chip ico"
                href={link.href}
                target="_blank"
                rel="noreferrer noopener"
                title={SOCIAL_TITLE[link.kind]}
                aria-label={SOCIAL_TITLE[link.kind]}
              >
                <SocialIcon kind={link.kind} />
              </a>
            ))}
            {/*
              The one scam check that costs nothing: how many other tokens
              wear this picture rides along on every pair.
            */}
            {reused > 0 ? (
              <span
                className="chip warn"
                title={`This picture is used by ${reused} other token${
                  reused === 1 ? '' : 's'
                }`}
              >
                <PictureIcon /> reused ×{reused}
              </span>
            ) : undefined}
          </span>
        </div>
      </div>

      {/*
        The figure, then the run. Naked cells at a wide gap, the way both of
        them do it — a border around each one is what made this read as a
        table rather than as a market.
      */}
      <button
        className="capfig"
        type="button"
        title="Show the chart in price instead"
        onClick={() =>
          setPriceType(priceType === 'market-cap' ? 'absolute' : 'market-cap')
        }
      >
        <b className="mono">{usd(cap)}</b>
        {Number.isFinite(change24h) ? (
          <em
            className="mono"
            style={{ color: toneVar(signedTone(change24h)) }}
          >
            {percent(change24h, { sign: true })}
          </em>
        ) : undefined}
      </button>

      <div className="figs" ref={figsRef}>
        <Fig label="Price" value={priceOf(price.priceUsd.toNumber())} />
        <Fig
          label="Liquidity"
          value={usd(liquidity)}
          note={share === undefined ? undefined : percent(share)}
          tone={liquidityTone(share)}
        />
        <Fig label="Vol 24h" value={usd(dayVolume.toNumber())} />
        <Fig
          label="Net 24h"
          value={signedUsd(net)}
          tone={signedTone(Number.isFinite(net) ? net : undefined)}
        />
        <Fig label="TX 24h" value={count(dayTx)} />
        <Fig label="Makers" value={count(dayMakers)} />
        <Fig label="Holders" value={count(Number(holdersCount))} />
        <Fig
          label="ATH"
          value={usd(ath)}
          note={fromHigh === undefined ? undefined : percent(fromHigh)}
          tone={signedTone(fromHigh)}
        />
        <Fig label="Supply" value={quantity(supply.toNumber())} />
        <Fig
          label="Fees"
          value={quantity(fees.toNumber())}
          note={pair.base.ticker}
        />
        {curve === undefined ? undefined : (
          <Fig
            label="B.Curve"
            value={percent(curve)}
            tone={curve >= 100 ? 'good' : 'quiet'}
          />
        )}

        <button
          className="fig asbtn"
          type="button"
          title={verdict.detail}
          onClick={() => setAuditOpen(!auditOpen)}
        >
          <i>Audit</i>
          <b className="mono" style={{ color: toneVar(verdict.tone) }}>
            {verdict.word}
          </b>
        </button>
      </div>

      <button
        className="figscroll"
        type="button"
        title="More figures"
        aria-label="Show the rest of the figures"
        onClick={() => figsRef.current?.scrollBy({ left: 220 })}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 6 L15 12 L9 18" />
        </svg>
      </button>

      {auditOpen ? <AuditChecks pair={pair} /> : undefined}
    </header>
  )
})

const SOCIAL_TITLE = { x: 'X', tg: 'Telegram', web: 'Website' } as const

/**
 * The three places a token says who it is.
 *
 * Drawn rather than lettered: `X` `TG` `WWW` as text read as three unrelated
 * abbreviations in a row already crowded with mono figures, and both terminals
 * put marks here.
 */
const SocialIcon: React.FC<{ kind: 'x' | 'tg' | 'web' }> = memo(({ kind }) => {
  if (kind === 'x') {
    return (
      <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.2 3 L21 3 L13.6 11.1 L22 21 L15.4 21 L10.6 15.2 L5.2 21 L1.4 21 L9.3 12.3 L1.2 3 L8 3 L12.3 8.3 Z" />
      </svg>
    )
  }
  if (kind === 'tg') {
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M21.5 3.5 L2.6 10.8 C1.8 11.1 1.8 11.7 2.5 11.9 L7.3 13.4 L18.4 6.4 C18.9 6.1 19.4 6.3 19 6.6 L10 14.8 L9.7 19.6 C10.1 19.6 10.3 19.4 10.5 19.2 L12.8 17 L17.7 20.6 C18.6 21.1 19.2 20.8 19.4 19.8 L22.3 5 C22.5 3.8 21.9 3.2 21.5 3.5 Z" />
      </svg>
    )
  }
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12 L21 12 M12 3 C15 7 15 17 12 21 C9 17 9 7 12 3" />
    </svg>
  )
})

const CrownIcon: React.FC = memo(() => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinejoin="round"
  >
    <path d="M3 7 L7.5 11 L12 4 L16.5 11 L21 7 L19 19 L5 19 Z" />
  </svg>
))

const PictureIcon: React.FC = memo(() => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinejoin="round"
  >
    <rect x="3" y="3" width="18" height="18" rx="3" />
    <circle cx="9" cy="9" r="2" />
    <path d="M21 15 L16 10 L5 21" />
  </svg>
))

const Fig: React.FC<{
  label: string
  value: string
  note?: string
  tone?: Tone
}> = memo(({ label, value, note, tone }) => (
  <div className="fig">
    <i>{label}</i>
    <b
      className="mono"
      style={tone && tone !== 'quiet' ? { color: toneVar(tone) } : undefined}
    >
      {value}
      {note ? <em className="mono">{note}</em> : undefined}
    </b>
  </div>
))

/**
 * Every check, once somebody asks which ones.
 *
 * The chip above is the answer; this is the working. Behind a click because a
 * trader reading a token in three seconds wants the word, and only the one
 * about to buy wants the list.
 */
const AuditChecks: React.FC<{ pair: PairModel }> = memo(({ pair }) => {
  const [audit] = useAtom(pair.audit)

  /* One of the seven is Solana's alone, so it is asked for rather than
     assumed — an EVM pool has six. */
  const mutable =
    'isTokenDataMutable' in audit.checks
      ? audit.checks.isTokenDataMutable
      : undefined

  const rows = [
    { label: 'Mintable', check: audit.checks.isMintable },
    { label: 'Freezable', check: audit.checks.isFreezable },
    ...(mutable ? [{ label: 'Metadata mutable', check: mutable }] : []),
    { label: 'Update authority', check: audit.checks.updateAuthority },
    { label: 'Top 10 holders', check: audit.checks.top10HoldersPercentage },
    { label: 'LP burned', check: audit.checks.lpBurned },
    { label: 'Dev holding', check: audit.checks.ownerTokenBalance },
  ]

  return (
    <div className="checks">
      {rows.map((row) => (
        <div className="ck" key={row.label}>
          <i>{row.label}</i>
          <b style={{ color: toneVar(statusTone(row.check.status)) }}>
            {checkValue(row.check)}
          </b>
        </div>
      ))}
    </div>
  )
})

const statusTone = (status: string): Tone => {
  if (status === 'Safe') {
    return 'good'
  }
  return status === 'Warning' ? 'watch' : 'bad'
}

/**
 * What a check came back with, in its own units.
 *
 * Some are a yes or a no, some are a share. Printing "true" for one and "31"
 * for the next is what made the old list read like a dump.
 */
const checkValue = (check: {
  status: string
  value?: unknown
  percent?: unknown
}): string => {
  if (check.percent !== undefined && check.percent !== null) {
    return percent(Number(check.percent))
  }
  if (typeof check.value === 'boolean') {
    return check.value ? 'Yes' : 'No'
  }
  if (typeof check.value === 'string' && check.value !== '') {
    /* Update authority answers with a whole address, which is the one check
       whose value is longer than the cell it lives in. */
    return check.value.length > 16 ? shortAddress(check.value, 4) : check.value
  }
  return check.status
}
